import React from "react";
import { DateTime } from "luxon";
import { getCurrentBrandHostnameAndEnv } from "../../../../domain/models/Enviroment";
import { doesErrorCodeExist } from "../../../../shared/src/codecs/errors";
import { requireExhaustive } from "../../../../shared/src/util";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../components/BuildingBlocks/FontSize";
import { MediaPropSwitch } from "../../components/BuildingBlocks/MediaPropSwitch";
import { Padding, TPaddingProps } from "../../components/BuildingBlocks/Padding";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { TextCenter } from "../../components/BuildingBlocks/TextCenter";
import { TextColor } from "../../components/BuildingBlocks/TextColor";
import { FrontAuthCodeInput } from "../../components/Front/Simple/FrontAuthCodeInput/FrontAuthCodeInput";
import { FrontCardTitle } from "../../components/Front/Simple/FrontCardTitle/FrontCardTitle";
import { FrontOutsideCard } from "../../components/Front/Simple/FrontOutsideCard/FrontOutsideCard";
import { FrontParagraphRegular } from "../../components/Front/Simple/FrontParagraphRegular/FrontParagraphRegular";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import FrontButtonPrimary from "../../components/Front/Simple/FrontButtonPrimary/FrontButtonPrimary";
import { FrontButtonSecondarySmall } from "../../components/Front/Simple/FrontButtonSecondarySmall/FrontButtonSecondarySmall";
import { Underline } from "../../components/Underline/Underline";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { WeightMedium } from "../../components/WeightMedium/WeightMedium";
import { FrontColors } from "../../models/FrontColors";
import { FrontFontSizes } from "../../models/FrontFontSizes";
import { FrontSpacing } from "../../models/FrontSpacing";
import { TContainerStateProps } from "../../state/TContainerStateProps";

const FrontAuthenticatorCardContainer = (props: TContainerStateProps): JSX.Element => {
    const page = props.state.forms.authenticator_page;
    const offer = page.children.begin_enrolment;
    const confirm = page.children.confirm_enrolment;

    const isEnrolled = () => page.edited.confirmed_at !== null;

    const enrolledOn = () =>
        page.edited.confirmed_at === null
            ? ""
            : DateTime.fromISO(page.edited.confirmed_at).toFormat("d MMMM yyyy");

    const hasOffer = () => offer.status === "success" && offer.output.secret !== "";

    const isCodeWrong = () => confirm.status === "validationError";

    const isAlreadyEnrolledElsewhere = () =>
        offer.status === "validationError"
        && doesErrorCodeExist("AuthenticatorAlreadyEnrolled", offer.validationErrors);

    const [confirmingRemoval, setConfirmingRemoval] = React.useState(false);

    // Kept in sync with each menu bar container's logoHref.
    const crmHomeHref = (): string => {
        const brand = getCurrentBrandHostnameAndEnv("web", window.location.hostname);

        switch (brand) {
            case "saillegal":
                return "/crm/legal/cases";
            case "sailprobate":
                return "/crm/probate/probate-helpline/callbacks";
            case "sailhomes":
                return "/crm/search";
            default:
                return requireExhaustive(brand);
        }
    };

    // The page has no menu bar, and browser-back is no escape either: the router pushes its
    // own entry, so back lands here again. A step-up bounce supplies the page to return to.
    const leaveHref = () =>
        props.state.routes.queryParams.VIEW_AUTH_AUTHENTICATOR.redirect_url || crmHomeHref();

    return (
        <FrontOutsideCard>
            <MediaPropSwitch<TPaddingProps>
                Component={Padding}
                mobile={{ type: "horizontal", spacing: "0px" }}
                desktop={{ type: "horizontal", spacing: FrontSpacing.MEDIUM_2 }}
                breakpoint="600"
            >
                <SpacingColumn spacing={FrontSpacing.LARGE_1}>
                    <TextCenter>
                        <FrontCardTitle>
                            Authenticator app
                        </FrontCardTitle>
                    </TextCenter>

                    {/* ALREADY SET UP */}
                    {isEnrolled() && !confirmingRemoval &&
                        <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                            <TextCenter>
                                <FrontParagraphRegular>
                                    <WeightBold>You set up an authenticator app on {enrolledOn()}.</WeightBold> <WeightBold>We'll ask for the code it shows</WeightBold> instead of texting you one.
                                </FrontParagraphRegular>
                            </TextCenter>

                            <SpacingRow justifyContent="center">
                                <FrontButtonSecondarySmall
                                    label="Remove this authenticator app"
                                    onClick={() => setConfirmingRemoval(true)}
                                />
                            </SpacingRow>
                        </SpacingColumn>
                    }

                    {/* REMOVING -- an explicit confirm, never a countdown: falling back to text
                        messages by mis-click is a downgrade the user would not notice. */}
                    {isEnrolled() && confirmingRemoval &&
                        <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                            <TextCenter>
                                <FrontParagraphRegular>
                                    <WeightBold>Remove your authenticator app?</WeightBold> <WeightBold>We'll go back to texting you a code</WeightBold> each time you sign in.
                                </FrontParagraphRegular>
                            </TextCenter>

                            <SpacingRow justifyContent="center" spacing={FrontSpacing.SMALL_3}>
                                <FrontButtonPrimary
                                    label="Yes, remove it"
                                    onClick={() => {
                                        setConfirmingRemoval(false);
                                        props.dispatch({type: "AUTHENTICATOR_REMOVE", payload: null});
                                    }}
                                />
                                <FrontButtonSecondarySmall
                                    label="Keep it"
                                    onClick={() => setConfirmingRemoval(false)}
                                />
                            </SpacingRow>
                        </SpacingColumn>
                    }

                    {/* NOT SET UP YET */}
                    {!isEnrolled() && !hasOffer() &&
                        <SpacingColumn spacing={FrontSpacing.MEDIUM_2}>
                            <TextCenter>
                                <FrontParagraphRegular>
                                    <WeightBold>Get your sign-in code from an app</WeightBold> like 1Password, Google Authenticator or Authy. <WeightBold>It takes a minute to set up</WeightBold> — scan a code, then type one back to us. <WeightBold>Then you never wait for a text again.</WeightBold>
                                </FrontParagraphRegular>
                            </TextCenter>

                            {isAlreadyEnrolledElsewhere() &&
                                <TextCenter>
                                    <FrontParagraphSmall>
                                        <TextColor color={FrontColors.ERROR_FIREBRICK_10}>
                                            <WeightMedium>
                                                You already have an authenticator app set up. Reload this page to see it.
                                            </WeightMedium>
                                        </TextColor>
                                    </FrontParagraphSmall>
                                </TextCenter>
                            }

                            <SpacingRow justifyContent="center">
                                <FrontButtonPrimary
                                    label="Set up an authenticator app"
                                    onClick={() => props.dispatch({type: "AUTHENTICATOR_BEGIN_ENROLMENT", payload: null})}
                                />
                            </SpacingRow>
                        </SpacingColumn>
                    }

                    {/* ENROLLING */}
                    {!isEnrolled() && hasOffer() &&
                        <SpacingColumn spacing={FrontSpacing.LARGE_1}>
                            <TextCenter>
                                <FrontParagraphRegular>
                                    <WeightBold>Scan this with your authenticator app.</WeightBold> <WeightBold>Or paste the setup link below</WeightBold> if you're using 1Password on this computer.
                                </FrontParagraphRegular>
                            </TextCenter>

                            <SpacingRow justifyContent="center">
                                <img
                                    src={offer.output.qr_data_uri}
                                    alt="QR code for setting up your authenticator app"
                                    width="180"
                                    height="180"
                                />
                            </SpacingRow>

                            <SpacingColumn spacing={FrontSpacing.SMALL_3}>
                                <TextCenter>
                                    <FrontParagraphSmall>
                                        Setup key
                                    </FrontParagraphSmall>
                                </TextCenter>
                                <TextCenter>
                                    <FontQuicksand>
                                        <FontSize size={FrontFontSizes.X_SMALL}>
                                            <WeightBold>{offer.output.secret}</WeightBold>
                                        </FontSize>
                                    </FontQuicksand>
                                </TextCenter>
                                <TextCenter>
                                    <FontQuicksand>
                                        <FontSize size={FrontFontSizes.X_SMALL}>
                                            {offer.output.otpauth_uri}
                                        </FontSize>
                                    </FontQuicksand>
                                </TextCenter>
                            </SpacingColumn>

                            <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                                <TextCenter>
                                    <FrontParagraphRegular>
                                        <WeightBold>Now type the 6-digit code your app shows</WeightBold> so we know it's working.
                                    </FrontParagraphRegular>
                                </TextCenter>

                                {isCodeWrong() &&
                                    <TextCenter>
                                        <FrontParagraphSmall>
                                            <TextColor color={FrontColors.ERROR_FIREBRICK_10}>
                                                <WeightMedium>
                                                    That code didn't match. Try the one showing now.
                                                </WeightMedium>
                                            </TextColor>
                                        </FrontParagraphSmall>
                                    </TextCenter>
                                }

                                <SpacingRow justifyContent="center">
                                    <FrontAuthCodeInput
                                        value={confirm.edited.code}
                                        onChange={(payload) => {
                                            props.dispatch({type: "AUTHENTICATOR_CONFIRM_CODE_CHANGE", payload});
                                            if (payload.length === 6) {
                                                props.dispatch({type: "AUTHENTICATOR_CONFIRM_ENROLMENT", payload: null});
                                            }
                                        }}
                                        displayError={isCodeWrong()}
                                    />
                                </SpacingRow>
                            </SpacingColumn>
                        </SpacingColumn>
                    }

                    <TextCenter>
                        <FontQuicksand>
                            <FontSize size={FrontFontSizes.X_SMALL}>
                                <a href={leaveHref()}>
                                    <Underline>
                                        <WeightMedium>
                                            Back to the CRM
                                        </WeightMedium>
                                    </Underline>
                                </a>
                            </FontSize>
                        </FontQuicksand>
                    </TextCenter>
                </SpacingColumn>
            </MediaPropSwitch>
        </FrontOutsideCard>
    );
};

export default FrontAuthenticatorCardContainer;
